import { useState, useRef, useCallback } from 'react';
import { withStyles } from '@material-ui/core';
import { isEqual } from 'lodash/lang';
import { debounce } from 'lodash';
import { useAbortController } from '../../../../../../common/hooks/useAbortController';
import { postData } from '../../../../../../../api/componentAPIs';
import { hasError, getPayload, isInvalidPayload } from './utils';
import appConstants from '../../../../../../../appConstants';
import { DuplicatePickupInfo, Header, DuplicatePickupStatus, Retry } from './components';
import { isCreatePickupMode } from '../utils';

const styles = (theme) => ({
  container: {
    flexWrap: 'wrap',
    border: `1px solid ${theme.AppBGColor}`,
    borderRadius: '4px',
    padding: 8,
    marginTop: 24,
    justifyContent: 'space-between'
  }
});

const DuplicatePickupChecker = ({
  classes,
  getSelectedPickupsByIds,
  editPickup,
  mergePickup,
  terminalCode,
  pickupData,
  data,
  error,
  mode,
  enablePickupEdit,
}) => {
  const [duplicatePickups, setDuplicatePickups] = useState([]);
  const [isPending, setIsPending] = useState(false);
  const [isApiError, setIsApiError] = useState(false);
  const prevDataRef = useRef(data);
  const { getNewAbortSignal, isMountedRef } = useAbortController();

  const isRMPickup = (duplicatePickups) => {
    return duplicatePickups.some((pickupData) => pickupData.rmaxPickupNumber && pickupData.rmaxPickupNumber === pickupData.pickupNumber);
  };

  const handleError = () => {
    setIsApiError(true);
    setIsPending(false);
  };

  const handleSuccess = (responseData) => {
    setDuplicatePickups(getSelectedPickupsByIds(responseData));
    setIsApiError(false);
    setIsPending(false);
  };

  const fetchDuplicatePickups = useCallback(() => {
    setIsPending(true);
    setDuplicatePickups([]);
    const payload = {
      terminalCode,
      pickupId: pickupData?.pickupId ?? null,
      shipperLatitude: pickupData?.shipperLatitude ?? null,
      shipperLongitude: pickupData?.shipperLatitude ?? null,
      ...getPayload(prevDataRef.current)
    };
    const apiName = "GetPickupDuplicates";
    const signal = getNewAbortSignal(apiName);
    postData(apiName, payload, { signal }).then((response) => {
      if (isMountedRef.current) {
        if (response?.status >= 200 && response?.status < 400) {
          handleSuccess(response.data);
        } else {
          handleError();
        }
      }
    });
  }, []);

  const debounceFetchDuplicatePickups = useCallback(
    debounce(fetchDuplicatePickups, appConstants.ONE_SECOND_TIME_OUT),
    [fetchDuplicatePickups]
  );

  if (hasError(error) || isInvalidPayload(data)) {
    return null;
  }

  if (!isEqual(prevDataRef.current, data)) {
    prevDataRef.current = data;
    setIsApiError(false);
    debounceFetchDuplicatePickups();
  }

  const duplicatePickupCount = duplicatePickups.length;
  if (isApiError) {
    return (
      <DuplicatePickupStatus>
        <Retry onClick={fetchDuplicatePickups} />
      </DuplicatePickupStatus>
    );
  } else if (duplicatePickupCount) {
    const isCreatePickupFlow = isCreatePickupMode(mode);
    return (
      <div className={classes.container} id="possibleDuplicates">
        <Header
          onMergePickupClick={() => {
            if (isCreatePickupFlow) {
              return mergePickup(duplicatePickups, true);
            }
            return mergePickup([pickupData, ...duplicatePickups], true);
          }}
          disableMerge={
            isRMPickup(duplicatePickups) ||
            (isCreatePickupFlow ? duplicatePickupCount === 1 : duplicatePickupCount === 0)
          }
        />
        {duplicatePickups.map((item, index) => (
          <DuplicatePickupInfo
            key={index}
            {...item}
            onEdit={(selectedPickupData) => editPickup([selectedPickupData])}
            enablePickupEdit={enablePickupEdit}
          />
        ))}
      </div>
    );
  }
  return isPending ? <DuplicatePickupStatus /> : null;
};

export default withStyles(styles)(DuplicatePickupChecker);
